//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { AxiosResponse } from 'axios';
import { AbortDiagnoseDto } from 'dto/abort-diagnose.dto';
import { BatchUpdateParSwitchPortsDto } from 'dto/batch-update-par-switch-ports.dto';
import { CbtUserUpdateDto } from 'dto/cbt-user-update.dto';
import { CopyConfigurationDto } from 'dto/copy-configuration.dto';
import { CreateParCaptivePortalProfileWithFileObjToString } from 'dto/create-par-captive-portal-profile.dto';
import { CreateParInterfaceDto } from 'dto/create-par-interface.dto';
import { CreateParRadiusProfileDto } from 'dto/create-par-radius-profile.dto';
import { CreateParSchedulingProfileDto } from 'dto/create-par-scheduling-profile.dto';
import { CreateParSsidDto } from 'dto/create-par-ssid.dto';
import { CreateParVlanProfileDto } from 'dto/create-par-vlan-profile.dto';
import { CreateRadioProfileDto } from 'dto/create-radio-profile.dto';
import { DeleteRadioProfileDto } from 'dto/delete-radio-profile.dto';
import { DiagnoseDto } from 'dto/diagnose.dto';
import { EapKickClientsInfoDto } from 'dto/eap-kick-clients-info.dto';
import { PostEventDto } from 'dto/post-event.dto';
import { PostNslookupDto } from 'dto/post-nslookup.dto';
import { PostPingDto } from 'dto/post-ping.dto';
import { PostTracerouteDto } from 'dto/post-traceroute.dto';
import { ResponseBinInfoDto } from 'dto/response-bin-info.dto';
import { ResponseParCaptivePortalProfileDto } from 'dto/response-par-captive-portal-profile.dto';
import { SetupCreateDto, SetupCreateDtoWithAccessToken } from 'dto/setup-create.dto';
import { SignInDtoWithFirstSetup } from 'dto/signin-with-first-setup.dto';
import { SignInDto } from 'dto/signin.dto';
import { SignInResponseDto } from 'dto/singin-response.dto';
import {
  ControllerQueryConfigData,
  ControllerQueryConfigDataDraft,
  UpdateControllerQueryConfigDataDto,
} from 'dto/update-controller-query-config-data.dto';
import { UpdateDiagnoseTimeoutDto } from 'dto/update-diagnose-timeout.dto';
import { UpdateEapProfilesDto } from 'dto/update-eap-profiles.dto';
import { EAPQueryConfigData, UpdateEAPQueryConfigDataDto } from 'dto/update-eap-query-config-data.dto';
import { UpdateParCaptivePortalProfileWithFileObjToString } from 'dto/update-par-captive-portal-profile.dto';
import { UpdateParControllerPortDto } from 'dto/update-par-controller-port.dto';
import { UpdateParDeviceNicknameDto } from 'dto/update-par-device-nickname.dto';
import { UpdateParEapPortDto } from 'dto/update-par-eap-port.dto';
import { UpdateParInterfaceDto } from 'dto/update-par-interface.dto';
import { UpdateParRadiusProfileDto } from 'dto/update-par-radius-profile.dto';
import { UpdateParSchedulingProfileDto } from 'dto/update-par-scheduling-profile.dto';
import { UpdateParSsidDto } from 'dto/update-par-ssid.dto';
import { UpdateParSwitchPortDto } from 'dto/update-par-switch-port.dto';
import { UpdateParVlanProfileDto } from 'dto/update-par-vlan-profile.dto';
import { UpdateRadioProfileDto } from 'dto/update-radio-profile.dto';
import { SwitchQueryConfigData, UpdateSwitchQueryConfigDataDto } from 'dto/update-switch-query-config-data.dto';
import { UpdateSystemParDeviceConfigDto } from 'dto/update-system-par-device-config.dto';
import { UpdateSystemTemplate } from 'dto/update-system-template.dto';
import { UpdateSystemDto } from 'dto/update-systems-query.dto';
import { UpsertParInterfaceArrayDto, UpsertParInterfaceDto } from 'dto/upsert-par-interface.dto';
import { LatencyDay } from 'entities/latency-day.entity';
import { ParVlanProfile } from 'entities/par-vlan-profile.entity';
import { ParseParCaptivePortal } from 'entities/parse-par-captive-portal';
import { ParseParControllerPort } from 'entities/parse-par-controller-port';
import { ParseParEapPort } from 'entities/parse-par-eap-port';
import { ParseParInterface } from 'entities/parse-par-interface';
import { ParseParRadio } from 'entities/parse-par-radio';
import { ParseParRadiusProfile } from 'entities/parse-par-radius-profile';
import { ParseParSchedulingProfile } from 'entities/parse-par-scheduling-profile';
import { ParseParSsid } from 'entities/parse-par-ssid';
import { ParseParSwitchPort } from 'entities/parse-par-switch-port';
import { ParseSystemWithoutParDeviceConfig } from 'entities/parse-system-without-par-device-config';
import { Task } from 'entities/task.entity';
import { User } from 'entities/user.entity';
import { CaptiveClientsStatus } from 'models/captive-clients-status';
import { CbtCaptiveClients } from 'models/cbt-captive-clients';
import { ClientQualityTimestampSnr } from 'models/client-quality-timestamp-snr';
import { CommandListKickResults } from 'models/command-list-kick-results';
import { CommandUuidResp } from 'models/command-uuid-resp';
import { ComplexDevice } from 'models/complex-device';
import { ControllerDetailInfo } from 'models/controller-detail-info';
import { ControllerFwUpgradeProgress } from 'models/controller-fw-upgrade-progress';
import { ControllerServiceDashboard } from 'models/controller-service-dashboard';
import { Dashboard } from 'models/dashboard';
import { DeviceVerInfo } from 'models/device-ver-info';
import { DhcpLeaseInfo } from 'models/dhcp-lease-info';
import { DiagnosticResultQuery } from 'models/diagnostic-result-query';
import { EapDetailInfo } from 'models/eap-detail-info';
import { EapInfoWithProfileName } from 'models/eap-info-with-profile-name';
import { EapProfiles } from 'models/eap-profiles';
import { EventList } from 'models/event-list';
import { FirmwareResponse } from 'models/firmware';
import { ClientInfoWithNetInfoUplinkNameAndLinkRate } from 'models/inner-topology-client';
import { NslookupResp } from 'models/nslookup-resp';
import { ParDeviceConfig } from 'models/par-device-config.model';
import { ParseCommandList } from 'models/parse-command-list';
import { PingResp } from 'models/ping-resp';
import { QoeScoreTotalList } from 'models/qoe-score-total-list';
import { ResponsePostDiagnose } from 'models/response-post-diagnose';
import { ResponseDiagnosticResult } from 'models/resposne-diagnostic-result';
import { RttyAuthorized } from 'models/rtty-authorized';
import { RttyFontSize } from 'models/rtty-fontsize';
import { RttyInfo } from 'models/rtty-info';
import { SetupResponseDto } from 'models/setup-response.dto';
import { SwitchDetailInfo } from 'models/switch-detail-info';
import { SwitchInfo } from 'models/switch-info';
import { SwitchStatisticCbtSwitchBindingTable } from 'models/switch-statistic-cbt-switch-bindingTable';
import { SwitchStatisticCbtSwitchLldp } from 'models/switch-statistic-cbt-switch-lldp';
import { SysLogsPagination } from 'models/sys-logs-pagination';
import { SysLogsRefreshResp } from 'models/sys-logs-refresh-resp';
import { SystemStatic } from 'models/system-statistic';
import { TopologyTreeNode } from 'models/topology-tree-node';
import { TracerouteResp } from 'models/traceroute-resp';
import { TssAggregateAppNameTxRx } from 'models/tss-aggregate-app-name-tx-rx';
import { TssAggregateSrcMacTxRx } from 'models/tss-aggregate-src-mac-tx-rx';
import { TssAggregateTimestampTxRx } from 'models/tss-aggregate-timestamp-tx-rx';
import { WanInfo } from 'models/wan-info';
import { WifiEnv } from 'models/wifi-env';
import { WifiScan } from 'models/wifi-scan';
import { WifiScanStatus } from 'models/wifi-scan_status';
import { DiagnosticType } from 'pages/Diagnostic';
import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from 'utils/axiosInstance';
import { getClientId } from 'utils/clientId';
import { BuildVersionList } from 'utils/helper';
import { getItem, setItem } from 'utils/localStorageHelper';
import { usePortalAuth } from './AuthPortalProvider';
import { useConfig } from './ConfigProvider';
import { useToast } from './ToastProvider';

const AuthContext = React.createContext<{
  token: string;
  user: User;
  currentLng: string;
  setCurrentLng: Dispatch<SetStateAction<string>>;
  networkDrafts: UpsertParInterfaceDto[];
  setNetworkDrafts: Dispatch<SetStateAction<UpsertParInterfaceDto[]>>;
  controllerQueryConfigDataDrafts: ControllerQueryConfigDataDraft | null;
  setControllerQueryConfigDataDrafts: Dispatch<SetStateAction<ControllerQueryConfigDataDraft | null>>;
  // api part
  signUpByUser: (params: SetupCreateDtoWithAccessToken) => Promise<AxiosResponse<SetupResponseDto>>;
  signUpByOldUser: (params: SetupCreateDtoWithAccessToken) => Promise<AxiosResponse<SetupResponseDto>>;
  signInByUser: (params: SignInDto) => Promise<AxiosResponse<SignInResponseDto>>;
  logout: () => void;
  updateTokenAfterLogin: (accessToken: string, refreshToken: string) => void;
  getUser: (accessToken?: string) => void;
  getWanInfo: () => Promise<AxiosResponse<WanInfo>>;
  getControllersConfigurationQueryBySerialNumber: (serialNumber: string, configs: string[]) => Promise<AxiosResponse<ControllerQueryConfigData>>;
  putControllersConfigurationQueryBySerialNumber: (
    serialNumber: string,
    configQuery: UpdateControllerQueryConfigDataDto
  ) => Promise<AxiosResponse<UpdateControllerQueryConfigDataDto>>;
  getEapsConfigurationQueryBySerialNumber: (serialNumber: string, configs: string[]) => Promise<AxiosResponse<EAPQueryConfigData>>;
  putEapsConfigurationQueryBySerialNumber: (
    serialNumber: string,
    configQuery: UpdateEAPQueryConfigDataDto
  ) => Promise<AxiosResponse<UpdateEAPQueryConfigDataDto>>;
  getSwitchsConfigurationQueryBySerialNumber: (serialNumber: string, configs: string[]) => Promise<AxiosResponse<SwitchQueryConfigData>>;
  putSwitchsConfigurationQueryBySerialNumber: (
    serialNumber: string,
    configQuery: UpdateSwitchQueryConfigDataDto
  ) => Promise<AxiosResponse<SwitchQueryConfigData>>;
  deleteDeviceBySerialNumber: (serialNumber: string) => Promise<AxiosResponse>;
  getRttyBySerialNumber: (serialNumber: string) => Promise<AxiosResponse<RttyInfo>>;
  getRttyAuthorized: (connectionId: string) => Promise<AxiosResponse<RttyAuthorized>>;
  getRttyFontSize: (connectionId: string) => Promise<AxiosResponse<RttyFontSize>>;
  getEaps: () => Promise<AxiosResponse<EapInfoWithProfileName[]>>;
  getEapProfiles: (serialNumber: string) => Promise<AxiosResponse<EapProfiles>>;
  putEapProfileMapIds: (serialNumber: string, data: UpdateEapProfilesDto) => Promise<AxiosResponse<EapProfiles>>;
  getEapBySerialNumber: (serialNumber: string) => Promise<AxiosResponse<EapDetailInfo>>;
  postEapConfigurationCopy: (serialNumber: string, copyConfigurationDto: CopyConfigurationDto) => Promise<AxiosResponse>;
  postCbtPing: (cbtPing: PostPingDto, serialNumber: string, signal: AbortSignal) => Promise<AxiosResponse<PingResp>>;
  postCbtTraceroute: (cbtTraceroute: PostTracerouteDto, serialNumber: string, signal: AbortSignal) => Promise<AxiosResponse<TracerouteResp>>;
  postCbtNslookup: (serialNumber: string, cbtNslookup: PostNslookupDto) => Promise<AxiosResponse<NslookupResp>>;
  getCbtWifiEnv: () => Promise<AxiosResponse<{ wifiEnv: WifiEnv[] }>>;
  getCbtWifiScan: () => Promise<AxiosResponse<WifiScanStatus>>;
  postCbtWifiScan: () => Promise<AxiosResponse<WifiScan>>;
  getTopologyTreeNode: () => Promise<AxiosResponse<TopologyTreeNode>>;
  getControllerInfo: (serialNumber: string) => Promise<AxiosResponse<ControllerDetailInfo>>;
  getClients: () => Promise<AxiosResponse<ClientInfoWithNetInfoUplinkNameAndLinkRate[]>>;
  getClientByMacAddr: (macAddr: string) => Promise<AxiosResponse<ClientInfoWithNetInfoUplinkNameAndLinkRate>>;
  getCaptiveClients: (kickingTag: boolean) => Promise<AxiosResponse<CbtCaptiveClients>>;
  getCaptiveClientsStatus: () => Promise<AxiosResponse<CaptiveClientsStatus>>;
  postCaptiveClients: (eapClientData: EapKickClientsInfoDto[]) => Promise<AxiosResponse<CommandListKickResults>>;
  getSwitches: () => Promise<AxiosResponse<SwitchInfo[]>>;
  getSwitchBySerialNumber: (serialNumber: string) => Promise<AxiosResponse<SwitchDetailInfo>>;
  postSwitchConfigurationCopy: (serialNumber: string, copyConfigurationDto: CopyConfigurationDto) => Promise<AxiosResponse>;
  getParSwitchPort: (switchSerialNumber: string, portNumber: number) => Promise<AxiosResponse<ParseParSwitchPort>>;
  putParSwitchPort: (
    switchSerialNumber: string,
    portNumber: number,
    cbtParSwitchPortConfig: UpdateParSwitchPortDto
  ) => Promise<AxiosResponse<ParseParSwitchPort>>;
  putParSwitchPorts: (
    switchSerialNumber: string,
    cbtParSwitchPortsConfig: BatchUpdateParSwitchPortsDto
  ) => Promise<AxiosResponse<ParseParSwitchPort>>;
  getParSwitchPortLldpStatus: (switchSerialNumber: string, portNumber: number) => Promise<AxiosResponse<SwitchStatisticCbtSwitchLldp[]>>;
  getParSwitchBindingTable: (switchSerialNumber: string) => Promise<AxiosResponse<SwitchStatisticCbtSwitchBindingTable[]>>;
  getCbtParSsids: () => Promise<AxiosResponse<ParseParSsid[]>>;
  putCbtParSsids: (id: number, cbtParSsidConfig: UpdateParSsidDto) => Promise<AxiosResponse<ParseParSsid>>;
  postCbtParSsids: (cbtParSsidConfig: CreateParSsidDto) => Promise<AxiosResponse<ParseParSsid>>;
  deleteCbtParSsids: (id: number) => Promise<AxiosResponse<ParseParSsid>>;
  getCbtParRadius: () => Promise<AxiosResponse<ParseParRadiusProfile[]>>;
  putCbtParRadius: (id: number, cbtParRadiusConfig: UpdateParRadiusProfileDto) => Promise<AxiosResponse<ParseParRadiusProfile>>;
  postCbtParRadius: (cbtParRadiusConfig: CreateParRadiusProfileDto) => Promise<AxiosResponse<ParseParRadiusProfile>>;
  deleteCbtParRadius: (id: number) => Promise<AxiosResponse<ParseParRadiusProfile>>;
  getCbtParCaptivePortal: () => Promise<AxiosResponse<ResponseParCaptivePortalProfileDto[]>>;
  deleteCbtParCaptivePortal: (id: number) => Promise<AxiosResponse<ParseParCaptivePortal>>;
  putCbtParCaptivePortal: (
    id: number,
    cbtParCaptivePortalConfig: UpdateParCaptivePortalProfileWithFileObjToString
  ) => Promise<AxiosResponse<ParseParCaptivePortal>>;
  postCbtParCaptivePortal: (
    cbtParCaptivePortalConfig: CreateParCaptivePortalProfileWithFileObjToString
  ) => Promise<AxiosResponse<ParseParCaptivePortal>>;
  getCbtParScheduling: () => Promise<AxiosResponse<ParseParSchedulingProfile[]>>;
  deleteCbtParScheduling: (id: number) => Promise<AxiosResponse<ParseParSchedulingProfile>>;
  putCbtParScheduling: (id: number, cbtParSchedulingConfig: UpdateParSchedulingProfileDto) => Promise<AxiosResponse<ParseParSchedulingProfile>>;
  postCbtParScheduling: (cbtParSchedulingConfig: CreateParSchedulingProfileDto) => Promise<AxiosResponse<ParseParSchedulingProfile>>;
  getCbtParRadios: () => Promise<AxiosResponse<ParseParRadio[]>>;
  putCbtParRadios: (cbtParRadioConfig: UpdateRadioProfileDto) => Promise<AxiosResponse<ParseParRadio[]>>;
  postCbtParRadios: (cbtParRadioConfig: CreateRadioProfileDto) => Promise<AxiosResponse<ParseParRadio[]>>;
  deleteCbtParRadios: (cbtParRadioConfig: DeleteRadioProfileDto) => Promise<AxiosResponse<ParseParRadio[]>>;
  getCbtFirmwareBySerialNumber: (serialNumber: string) => Promise<AxiosResponse>;
  postCbtFirmwareBySerialNumber: (serialNumber: string) => Promise<AxiosResponse>;
  getCheckNewestFirmwareBySerialNumber: (serialNumber: string) => Promise<AxiosResponse>;
  postUpgradeFirmwareBySerialNumber: (serialNumber: string, fwInfo: FirmwareResponse) => Promise<AxiosResponse>;
  postLedsBySerialNumber: (serialNumber: string) => Promise<AxiosResponse>;
  postOnBoardBySerialNumber: (serialNumber: string) => Promise<AxiosResponse>;
  postRebootBySerialNumber: (serialNumber: string) => Promise<AxiosResponse>;
  postScheduleRebootBySerialNumber: (serialNumber: string, scheduleTime: number) => Promise<AxiosResponse>;
  postFactoryBySerialNumber: (serialNumber: string) => Promise<AxiosResponse>;
  getDashboard: () => Promise<AxiosResponse<Dashboard>>;
  getDashboardServices: () => Promise<AxiosResponse<ControllerServiceDashboard>>;
  getParInterfaces: (serialNumber: string) => Promise<AxiosResponse<ParseParInterface[]>>;
  postParInterfaces: (cbtNewParInterfaceConfig: CreateParInterfaceDto) => Promise<AxiosResponse<ParseParInterface>>;
  postParInterfacesListBySerialNumber: (
    serialNumber: string,
    cbtNewParInterfaceArrayConfig: UpsertParInterfaceArrayDto
  ) => Promise<AxiosResponse<ParseParInterface>>;
  putParInterfaces: (id: number, cbtParInterfacesConfig: UpdateParInterfaceDto) => Promise<AxiosResponse<ParseParInterface>>;
  deleteParInterfaces: (id: number) => Promise<AxiosResponse<ParseParInterface>>;
  deleteParInterfacesListBySerialNumber: (serialNumber: string, idList: number[]) => Promise<AxiosResponse<ParseParInterface>>;
  getParVlanProfiles: () => Promise<AxiosResponse<ParVlanProfile[]>>;
  postParVlanProfile: (data: CreateParVlanProfileDto) => Promise<AxiosResponse<ParVlanProfile>>;
  updateParVlanProfile: (id: number, data: UpdateParVlanProfileDto) => Promise<AxiosResponse<ParVlanProfile>>;
  deleteParVlanProfileById: (id: number) => Promise<AxiosResponse<ParVlanProfile>>;
  getBackupRestoreConfigurationByModelName: () => Promise<AxiosResponse>;
  putBackupRestoreConfigurationByModelName: (file: FormData) => Promise<AxiosResponse>;
  putCbtUserUpdate: (cbtConfig: CbtUserUpdateDto) => Promise<AxiosResponse>;
  putUserRole: (id: number, roleObject: { role: string }) => Promise<AxiosResponse>;
  putCbtDeviceName: (serialNumber: string, cbtDeviceName: UpdateParDeviceNicknameDto) => Promise<AxiosResponse>;
  getEventList: (startDate: number, endDate: number) => Promise<AxiosResponse<EventList>>;
  postEvent: (event: PostEventDto) => Promise<AxiosResponse>;
  getQoeScoreTotalList: (startDate: number, endDate: number) => Promise<AxiosResponse<QoeScoreTotalList>>;
  postUsersBindCloudAccout: (data: SetupCreateDto) => Promise<AxiosResponse>;
  postUsersUnBindCloudAccout: (data: SignInDto) => Promise<AxiosResponse>;
  getDeviceMembers: () => Promise<AxiosResponse<User[]>>;
  getParControllerPort: (controllerSerialNumber: string, portNumber: number) => Promise<AxiosResponse<ParseParControllerPort>>;
  putParControllerPort: (
    controllerSerialNumber: string,
    portNumber: number,
    cbtParControllerPortConfig: UpdateParControllerPortDto
  ) => Promise<AxiosResponse<ParseParControllerPort>>;
  getParEapPort: (eapSerialNumber: string, portNumber: number) => Promise<AxiosResponse<ParseParEapPort>>;
  putParEapPort: (
    eapSerialNumber: string,
    portNumber: number,
    cbtParControllerPortConfig: UpdateParEapPortDto
  ) => Promise<AxiosResponse<ParseParEapPort>>;
  getTssAggregateSumByTimestamp: (type: 'day' | 'week' | 'month', identified?: number) => Promise<AxiosResponse<TssAggregateTimestampTxRx[]>>;
  getTssAggregateSumByAppName: (type: 'day' | 'week' | 'month') => Promise<AxiosResponse<TssAggregateAppNameTxRx[]>>;
  postTssAggregateSumByAppNameQuery: (type: 'day' | 'week' | 'month', srcMacs: string[]) => Promise<AxiosResponse<TssAggregateAppNameTxRx[]>>;
  getTssAggregateSumBySrcMac: (type: 'day' | 'week' | 'month') => Promise<AxiosResponse<TssAggregateSrcMacTxRx[]>>;
  getSystemStatisticDays: (serialNumber: string) => Promise<AxiosResponse<SystemStatic[]>>;
  getLatencyDays: () => Promise<AxiosResponse<LatencyDay[]>>;
  getClientQuality: (type: 'day' | 'week' | 'month', macAddr: string, connectType: number) => Promise<AxiosResponse<ClientQualityTimestampSnr[]>>;
  getClientAllQuality: (type: 'day' | 'week' | 'month', macAddr: string) => Promise<AxiosResponse<ClientQualityTimestampSnr[]>>;
  getDeviceVerInfo: () => Promise<AxiosResponse<DeviceVerInfo>>;
  getTaskCheckAndUpgrades: () => Promise<AxiosResponse<Task[]>>;
  getSystems: () => Promise<AxiosResponse<ParseSystemWithoutParDeviceConfig>>;
  getSystemsParDeviceConfig: (configs: string[]) => Promise<AxiosResponse<ParDeviceConfig>>;
  putSystemsQuery: (systemQuery: UpdateSystemDto) => Promise<AxiosResponse<ParseSystemWithoutParDeviceConfig>>;
  putSystemTemplateQuery: (systemTemplateQuery: UpdateSystemTemplate) => Promise<AxiosResponse<ParseSystemWithoutParDeviceConfig>>;
  putSystemsParDeviceConfigQuery: (parDeviceConfigQuery: UpdateSystemParDeviceConfigDto) => Promise<AxiosResponse<ParDeviceConfig>>;
  getSystemesSiteName: () => Promise<AxiosResponse<{ siteName: string }>>;
  getSysLogs: ({
    page,
    limit,
    sortBy,
    orderBy,
    searchColumn,
    searchContent,
  }: {
    page: number;
    limit: number;
    sortBy?: string;
    orderBy?: string;
    searchColumn?: string;
    searchContent?: string;
  }) => Promise<AxiosResponse<SysLogsPagination>>;
  getSysLogDownload: () => Promise<AxiosResponse>;
  getSysLogSendEmail: () => Promise<AxiosResponse>;
  getSysLogsByCriteria: (criteriaDto: any) => Promise<AxiosResponse<SysLogsPagination>>;
  getSysLogsRefresh: () => Promise<AxiosResponse<SysLogsRefreshResp>>;
  getCbtPoePowerCycle: (commandUuid: string) => Promise<AxiosResponse<ParseCommandList>>;
  postCbtPoePowerCycle: (serialNumber: string, portNumber: number) => Promise<AxiosResponse<CommandUuidResp>>;
  getCbtCableDiagnostics: (commandUuid: string) => Promise<AxiosResponse<ParseCommandList>>;
  postCbtCableDiagnostics: (serialNumber: string, portNumber: number) => Promise<AxiosResponse<CommandUuidResp>>;
  getCheckOwgwApiIsReady: () => Promise<AxiosResponse<boolean>>;
  getControllerDhcpLeasesBySerialNumber: (serialNumber: string) => Promise<AxiosResponse<DhcpLeaseInfo[]>>;
  getCheckApiIsReady: () => Promise<AxiosResponse<boolean>>;
  getCbtControllerFwUpgradeProgress: (serialNumber: string) => Promise<ControllerFwUpgradeProgress>;
  getCheckNewestBin: () => Promise<AxiosResponse>;
  postUpgradeBin: (fwInfo: FirmwareResponse) => Promise<AxiosResponse>;
  postResetBin: () => Promise<AxiosResponse>;
  postUploadBin: (file: FormData) => Promise<AxiosResponse>;
  getCheckUpgradeBinIsCompleted: () => Promise<AxiosResponse<boolean>>;
  getCheckResetBinIsCompleted: () => Promise<AxiosResponse<boolean>>;
  getCheckUploadBinIsCompleted: () => Promise<AxiosResponse<boolean>>;
  getBinInfo: () => Promise<AxiosResponse<ResponseBinInfoDto>>;
  getOnboardedDevices: () => Promise<AxiosResponse<ComplexDevice[]>>;
  getCbtDiagnoseResult: (diagnosticResultQuery: DiagnosticResultQuery) => Promise<AxiosResponse<ResponseDiagnosticResult>>;
  postPingDevice: (serialNumber: string, isDiagnose: boolean) => Promise<AxiosResponse>;
  postCbtDiagnostic: (diagnoseDto: DiagnoseDto) => Promise<AxiosResponse<ResponsePostDiagnose>>;
  postAbortCbtDiagnostic: (abortDiagnosticDto: AbortDiagnoseDto) => Promise<AxiosResponse<ResponseDiagnosticResult>>;
  putCbtDiagnosticTimeout: (timeoutDto: UpdateDiagnoseTimeoutDto) => Promise<AxiosResponse<ResponseDiagnosticResult>>;
  getAiFae: () => void;
}>(null!);

export const AuthProvider = ({ siteId, children }: { siteId?: number; children: React.ReactNode }) => {
  const { buildVersion, cbtApiUrl, cloudUrl, rootControllerSerialNumber, countryCode, getDefaultConfig, aifaeDomain } = useConfig();
  const { token: portalToken, storageClearThenGoBack } = usePortalAuth();
  const { startSse } = useToast();
  const i18nextLng = getItem('i18nextLng') ?? 'en';
  const { search } = useLocation();
  const [token, setToken] = useState<string>(getItem('access_token') ?? '');
  const [currentLng, setCurrentLng] = useState<string>(i18nextLng);
  const [networkDrafts, setNetworkDrafts] = useState(JSON.parse(getItem('networkDrafts') ?? '[]'));
  const [controllerQueryConfigDataDrafts, setControllerQueryConfigDataDrafts] = useState(() => {
    try {
      const savedDraft = getItem('controllerQueryConfigDataDrafts');
      return savedDraft ? JSON.parse(savedDraft) : null;
    } catch (error) {
      return null;
    }
  });
  const [user, setUser] = useState<User>({} as User);
  const requestInterceptorIdRef = useRef<number | null>(null);
  const responseInterceptorIdRef = useRef<number | null>(null);

  useEffect(() => {
    requestInterceptorIdRef.current = axiosInstance.interceptors.request.use(
      (config: { headers: { Authorization: string; Accept: string; ['Ronto-SiteId']: string; ['Ronto-ClientId']: string } }) => {
        // 每次發 API 都會從 localStorage 拿最新的 access_token
        // 若改成 react state 紀錄的 token，只會拿到初始值 '' 而不再更新
        config.headers.Authorization = `Bearer ${getItem('access_token')}`;
        config.headers.Accept = 'application/json';
        config.headers['Ronto-SiteId'] = getItem('siteId') ?? '';
        config.headers['Ronto-ClientId'] = getClientId();
        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      }
    );

    return () => {
      if (requestInterceptorIdRef.current !== null) {
        axiosInstance.interceptors.request.eject(requestInterceptorIdRef.current);
      }
    };
  }, []);

  useEffect(() => {
    /* NOTE: 非 portal UI 時，從 backend-base 做 refresh access token */
    if (buildVersion !== BuildVersionList.portal) {
      if (responseInterceptorIdRef.current !== null) {
        axiosInstance.interceptors.response.eject(responseInterceptorIdRef.current);
      }

      responseInterceptorIdRef.current = axiosInstance.interceptors.response.use(
        // Success actions
        undefined,
        async (error: any) => {
          if (!error.response) {
            return Promise.reject(error);
          }
          switch (error.response.status) {
            case 401:
              const newToken = await refreshAccessToken();
              // 使用新 Token 重新發送原始請求
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return axiosInstance.request(error.config);
            default:
              break;
          }
          return Promise.reject(error);
        }
      );
    }

    return () => {
      if (responseInterceptorIdRef.current !== null) {
        axiosInstance.interceptors.response.eject(responseInterceptorIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildVersion]);

  const signUpByUser = (params: SetupCreateDtoWithAccessToken) => {
    const { name, email, password } = params;
    return axiosInstance.post<SetupResponseDto>(`${cbtApiUrl}api/users/setup/create`, { name, email, password });
  };

  const signUpByOldUser = (params: SetupCreateDtoWithAccessToken) => {
    const { email, password, portalHost, portalPort, siteName, siteDescription } = params;
    return axiosInstance.post<SetupResponseDto>(`${cbtApiUrl}api/users/setup/login`, {
      email,
      password,
      portalHost,
      portalPort,
      siteName,
      siteDescription,
    });
  };

  const signInByUser = (params: SignInDto | SignInDtoWithFirstSetup) => {
    return axiosInstance.post<SignInResponseDto>(`${cbtApiUrl}api/oauth2`, params);
  };

  const signinByCloud = () => {
    const tokenFromCloud = new URLSearchParams(search).get('access_token');
    const refreshFromCloud = new URLSearchParams(search).get('refresh_token');
    // NOTE: useEffect 中判斷只要有 cbtApiUrl && search.length > 0 就會進來這個 function
    // NOTE: 若日後要新增 signinByCloud 相關邏輯，一律寫到 if 內，否則 if 外的邏輯會在不該執行時執行造成錯誤
    if (tokenFromCloud && refreshFromCloud) {
      setItem('isFromCloud', true);
      updateTokenAfterLogin(tokenFromCloud, refreshFromCloud);
      return getUser(tokenFromCloud)
        .then((response: any) => {
          if (response === undefined) throw new Error();
          window.location.replace('/');
        })
        .catch(() => {
          // if token is not correct，go back to cloud login page
          logout();
        });
    }
  };

  const logout = () => {
    if (buildVersion === BuildVersionList.portal) {
      storageClearThenGoBack();
    } else {
      const goBackUrl = getItem('isFromCloud') ? cloudUrl : '/';
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('isFromCloud');
      sessionStorage.clear();
      window.location.replace(goBackUrl);
    }
  };

  const updateTokenAfterLogin = (accessToken: string, refreshToken: string) => {
    setItem('access_token', accessToken);
    setItem('refresh_token', refreshToken);
    setToken(accessToken);
  };

  const refreshAccessToken = () => {
    return axiosInstance
      .get(`${JSON.parse(getItem('defaultConfig') ?? '{}')?.cbtApiUrl}api/oauth2/refreshToken/${getItem('refresh_token')}`)
      .then((response: AxiosResponse<{ access_token: 'string' }>) => {
        const newToken = response.data.access_token;
        setItem('access_token', newToken);
        setToken(newToken);
        return newToken;
      })
      .catch((error: any) => {
        console.error('Failed to refresh access token:', error);
        logout();
      });
  };

  const getUser = (accessToken?: string) => {
    return axiosInstance
      .get(`${cbtApiUrl}api/users/me`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken ?? token}`,
        },
      })
      .then((response: AxiosResponse<User>) => {
        startSse();
        // Reset currentLng
        setCurrentLng(i18nextLng);
        setUser(response.data);
        setItem('currentRole', response.data.role);
        // clear token on url then reload
        if (getItem('isFromCloud')) {
          return response.data;
        }
      })
      .catch(() => {});
  };

  const getWanInfo = (): Promise<AxiosResponse<WanInfo>> => {
    return axiosInstance.get<WanInfo>(`${cbtApiUrl}api/controllers/${rootControllerSerialNumber}/wanInfo`);
  };

  const getControllersConfigurationQueryBySerialNumber = (
    serialNumber: string,
    configs: string[]
  ): Promise<AxiosResponse<ControllerQueryConfigData>> => {
    let baseUrl = `${cbtApiUrl}api/controllers/${serialNumber}/configuration/query?`;

    if (configs.length > 0) {
      const configsStr = configs.map((param) => `c=${param}`).join('&');
      baseUrl += configsStr;
    }

    return axiosInstance.get<ControllerQueryConfigData>(baseUrl);
  };

  const putControllersConfigurationQueryBySerialNumber = (
    serialNumber: string,
    configQuery: UpdateControllerQueryConfigDataDto
  ): Promise<AxiosResponse<UpdateControllerQueryConfigDataDto>> => {
    return axiosInstance.put<UpdateControllerQueryConfigDataDto>(
      `${cbtApiUrl}api/controllers/${serialNumber}/configuration/updateQuery`,
      configQuery
    );
  };

  const getEapsConfigurationQueryBySerialNumber = (serialNumber: string, configs: string[]): Promise<AxiosResponse<EAPQueryConfigData>> => {
    let baseUrl = `${cbtApiUrl}api/eaps/${serialNumber}/configuration/query?`;

    if (configs.length > 0) {
      const configsStr = configs.map((param) => `c=${param}`).join('&');
      baseUrl += configsStr;
    }

    return axiosInstance.get<EAPQueryConfigData>(baseUrl);
  };

  const putEapsConfigurationQueryBySerialNumber = (
    serialNumber: string,
    configQuery: UpdateEAPQueryConfigDataDto
  ): Promise<AxiosResponse<UpdateEAPQueryConfigDataDto>> => {
    return axiosInstance.put<UpdateEAPQueryConfigDataDto>(`${cbtApiUrl}api/eaps/${serialNumber}/configuration/updateQuery`, configQuery);
  };

  const getSwitchsConfigurationQueryBySerialNumber = (serialNumber: string, configs: string[]): Promise<AxiosResponse<SwitchQueryConfigData>> => {
    let baseUrl = `${cbtApiUrl}api/switches/${serialNumber}/configuration/query?`;

    if (configs.length > 0) {
      const configsStr = configs.map((param) => `c=${param}`).join('&');
      baseUrl += configsStr;
    }

    return axiosInstance.get<SwitchQueryConfigData>(baseUrl);
  };

  const putSwitchsConfigurationQueryBySerialNumber = (
    serialNumber: string,
    configQuery: UpdateSwitchQueryConfigDataDto
  ): Promise<AxiosResponse<SwitchQueryConfigData>> => {
    return axiosInstance.put<SwitchQueryConfigData>(`${cbtApiUrl}api/switches/${serialNumber}/configuration/updateQuery`, configQuery);
  };

  const deleteDeviceBySerialNumber = (serialNumber: string): Promise<AxiosResponse> => {
    return axiosInstance.delete(`${cbtApiUrl}api/par-devices/${serialNumber}`);
  };

  const getRttyBySerialNumber = (serialNumber: string): Promise<AxiosResponse<RttyInfo>> => {
    return axiosInstance.get<RttyInfo>(`${cbtApiUrl}api/owgw/rtty/${serialNumber}`);
  };

  const getRttyAuthorized = (connectionId: string): Promise<AxiosResponse<RttyAuthorized>> => {
    return axiosInstance.get<RttyAuthorized>(`${cbtApiUrl}api/owgw/rtty/authorized/${connectionId}`, {});
  };

  const getRttyFontSize = (connectionId: string): Promise<AxiosResponse<RttyFontSize>> => {
    return axiosInstance.get<RttyFontSize>(`${cbtApiUrl}api/owgw/rtty/fontsize/${connectionId}`, {});
  };

  const getEaps = (): Promise<AxiosResponse<EapInfoWithProfileName[]>> => {
    return axiosInstance.get<EapInfoWithProfileName[]>(`${cbtApiUrl}api/eaps`);
  };

  const getEapProfiles = (serialNumber: string): Promise<AxiosResponse<EapProfiles>> => {
    return axiosInstance.get<EapProfiles>(`${cbtApiUrl}api/eaps/${serialNumber}/profiles`);
  };

  const putEapProfileMapIds = (serialNumber: string, data: UpdateEapProfilesDto): Promise<AxiosResponse<EapProfiles>> => {
    return axiosInstance.put<EapProfiles>(`${cbtApiUrl}api/eaps/${serialNumber}/profileMapIds`, data);
  };

  const getEapBySerialNumber = (serialNumber: string): Promise<AxiosResponse<EapDetailInfo>> => {
    return axiosInstance.get<EapDetailInfo>(`${cbtApiUrl}api/eaps/${serialNumber}/info`);
  };

  const postEapConfigurationCopy = (serialNumber: string, copyConfigurationDto: CopyConfigurationDto): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/eaps/${serialNumber}/configuration/copy`, copyConfigurationDto);
  };

  const postCbtPing = (cbtPing: PostPingDto, serialNumber: string, signal: AbortSignal): Promise<AxiosResponse<PingResp>> => {
    return axiosInstance.post<PingResp>(`${cbtApiUrl}api/owgw/ping/${serialNumber}`, cbtPing, {
      signal: signal,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const postCbtTraceroute = (cbtTraceroute: PostTracerouteDto, serialNumber: string, signal: AbortSignal): Promise<AxiosResponse<TracerouteResp>> => {
    return axiosInstance.post<TracerouteResp>(`${cbtApiUrl}api/owgw/traceroute/${serialNumber}`, cbtTraceroute, {
      signal: signal,
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const postCbtNslookup = (serialNumber: string, cbtNslookup: PostNslookupDto): Promise<AxiosResponse<NslookupResp>> => {
    return axiosInstance.post<NslookupResp>(`${cbtApiUrl}api/owgw/nslookup/${serialNumber}`, cbtNslookup, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const getCbtWifiEnv = (): Promise<AxiosResponse<{ wifiEnv: WifiEnv[] }>> => {
    return axiosInstance.get<{ wifiEnv: WifiEnv[] }>(`${cbtApiUrl}api/owgw/wifiEnv`);
  };

  const getCbtWifiScan = (): Promise<AxiosResponse<WifiScanStatus>> => {
    return axiosInstance.get<WifiScanStatus>(`${cbtApiUrl}api/owgw/wifiScan`);
  };

  const postCbtWifiScan = (): Promise<AxiosResponse<WifiScan>> => {
    return axiosInstance.post<WifiScan>(`${cbtApiUrl}api/owgw/wifiScan`, {});
  };

  const getTopologyTreeNode = (): Promise<AxiosResponse<TopologyTreeNode>> => {
    return axiosInstance.get<TopologyTreeNode>(`${cbtApiUrl}api/owgw/topology/treeNode`);
  };

  const getControllerInfo = (serialNumber: string): Promise<AxiosResponse<ControllerDetailInfo>> => {
    return axiosInstance.get<ControllerDetailInfo>(`${cbtApiUrl}api/controllers/${serialNumber}/info`);
  };

  const getClients = (): Promise<AxiosResponse<ClientInfoWithNetInfoUplinkNameAndLinkRate[]>> => {
    return axiosInstance.get<ClientInfoWithNetInfoUplinkNameAndLinkRate[]>(`${cbtApiUrl}api/clients`);
  };

  const getClientByMacAddr = (macAddr: string): Promise<AxiosResponse<ClientInfoWithNetInfoUplinkNameAndLinkRate>> => {
    return axiosInstance.get<ClientInfoWithNetInfoUplinkNameAndLinkRate>(`${cbtApiUrl}api/clients/${encodeURIComponent(macAddr)}/info`);
  };

  const getCaptiveClients = (kickingTag: boolean): Promise<AxiosResponse<CbtCaptiveClients>> => {
    return axiosInstance.get<CbtCaptiveClients>(`${cbtApiUrl}api/clients/captiveClients`, {
      params: { kickingTag },
    });
  };

  const getCaptiveClientsStatus = (): Promise<AxiosResponse<CaptiveClientsStatus>> => {
    return axiosInstance.get<CaptiveClientsStatus>(`${cbtApiUrl}api/clients/captiveClientsStatus`);
  };

  const postCaptiveClients = (eapClientData: EapKickClientsInfoDto[]): Promise<AxiosResponse<CommandListKickResults>> => {
    return axiosInstance.post<ParseParSwitchPort>(`${cbtApiUrl}api/clients/captiveClients`, eapClientData, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  // TODO:
  const getSwitches = (): Promise<AxiosResponse<SwitchInfo[]>> => {
    return axiosInstance.get<SwitchInfo[]>(`${cbtApiUrl}api/switches`);
  };
  // TODO:
  const getSwitchBySerialNumber = (seiralNumber: string): Promise<AxiosResponse<SwitchDetailInfo>> => {
    return axiosInstance.get<SwitchDetailInfo>(`${cbtApiUrl}api/switches/${seiralNumber}/info`);
  };

  const postSwitchConfigurationCopy = (serialNumber: string, copyConfigurationDto: CopyConfigurationDto): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/switches/${serialNumber}/configuration/copy`, copyConfigurationDto);
  };

  const getParSwitchPort = (switchSerialNumber: string, portNumber: number): Promise<AxiosResponse<ParseParSwitchPort>> => {
    return axiosInstance.get<ParseParSwitchPort>(`${cbtApiUrl}api/par-switch-ports/${switchSerialNumber}/port/${portNumber}`);
  };

  const putParSwitchPort = (
    switchSerialNumber: string,
    portNumber: number,
    cbtParSwitchPortConfig: UpdateParSwitchPortDto
  ): Promise<AxiosResponse<ParseParSwitchPort>> => {
    return axiosInstance.put<ParseParSwitchPort>(`${cbtApiUrl}api/par-switch-ports/${switchSerialNumber}/port/${portNumber}`, cbtParSwitchPortConfig);
  };

  const putParSwitchPorts = (
    switchSerialNumber: string,
    cbtParSwitchPortsConfig: BatchUpdateParSwitchPortsDto
  ): Promise<AxiosResponse<ParseParSwitchPort>> => {
    return axiosInstance.put<ParseParSwitchPort>(`${cbtApiUrl}api/par-switch-ports/${switchSerialNumber}/portList`, cbtParSwitchPortsConfig);
  };

  const getParSwitchPortLldpStatus = (switchSerialNumber: string, portNumber: number): Promise<AxiosResponse<SwitchStatisticCbtSwitchLldp[]>> => {
    return axiosInstance.get<SwitchStatisticCbtSwitchLldp[]>(`${cbtApiUrl}api/par-switch-ports/${switchSerialNumber}/port/${portNumber}/lldp`);
  };

  const getParSwitchBindingTable = (switchSerialNumber: string): Promise<AxiosResponse<SwitchStatisticCbtSwitchBindingTable[]>> => {
    return axiosInstance.get<SwitchStatisticCbtSwitchBindingTable[]>(`${cbtApiUrl}api/switches/${switchSerialNumber}/bindingTable`);
  };

  const getCbtParSsids = (): Promise<AxiosResponse<ParseParSsid[]>> => {
    return axiosInstance.get<ParseParSsid[]>(`${cbtApiUrl}api/par-ssids`);
  };

  const putCbtParSsids = (id: number, cbtParSsidConfig: UpdateParSsidDto): Promise<AxiosResponse<ParseParSsid>> => {
    return axiosInstance.put<ParseParSsid>(`${cbtApiUrl}api/par-ssids/${id}`, cbtParSsidConfig);
  };

  const postCbtParSsids = (cbtParSsidConfig: CreateParSsidDto): Promise<AxiosResponse<ParseParSsid>> => {
    return axiosInstance.post<ParseParSsid>(`${cbtApiUrl}api/par-ssids`, cbtParSsidConfig);
  };

  const deleteCbtParSsids = (id: number): Promise<AxiosResponse<ParseParSsid>> => {
    return axiosInstance.delete<ParseParSsid>(`${cbtApiUrl}api/par-ssids/${id}`);
  };

  const getCbtParRadius = useCallback((): Promise<AxiosResponse<ParseParRadiusProfile[]>> => {
    return axiosInstance.get<ParseParRadiusProfile[]>(`${cbtApiUrl}api/par-radius-profiles`);
  }, [cbtApiUrl]);

  const putCbtParRadius = (id: number, cbtParRadiusConfig: UpdateParRadiusProfileDto): Promise<AxiosResponse<ParseParRadiusProfile>> => {
    return axiosInstance.put<ParseParRadiusProfile>(`${cbtApiUrl}api/par-radius-profiles/${id}`, cbtParRadiusConfig);
  };

  const postCbtParRadius = (cbtParRadiusConfig: CreateParRadiusProfileDto): Promise<AxiosResponse<ParseParRadiusProfile>> => {
    return axiosInstance.post<ParseParRadiusProfile>(`${cbtApiUrl}api/par-radius-profiles`, cbtParRadiusConfig);
  };

  const deleteCbtParRadius = (id: number): Promise<AxiosResponse<ParseParRadiusProfile>> => {
    return axiosInstance.delete<ParseParRadiusProfile>(`${cbtApiUrl}api/par-radius-profiles/${id}`);
  };

  const getCbtParCaptivePortal = (): Promise<AxiosResponse<ResponseParCaptivePortalProfileDto[]>> => {
    return axiosInstance.get<ResponseParCaptivePortalProfileDto[]>(`${cbtApiUrl}api/par-captive-portals`);
  };

  const deleteCbtParCaptivePortal = (id: number): Promise<AxiosResponse<ParseParCaptivePortal>> => {
    return axiosInstance.delete<ParseParCaptivePortal>(`${cbtApiUrl}api/par-captive-portals/${id}`);
  };

  const putCbtParCaptivePortal = (
    id: number,
    cbtParCaptivePortalConfig: UpdateParCaptivePortalProfileWithFileObjToString
  ): Promise<AxiosResponse<ParseParCaptivePortal>> => {
    return axiosInstance.put<UpdateParCaptivePortalProfileWithFileObjToString>(
      `${cbtApiUrl}api/par-captive-portals/${id}`,
      cbtParCaptivePortalConfig,
      {
        headers: {
          'content-type': 'multipart/form-data',
          Accept: '*/*',
          Authorization: `Bearer ${token}`,
        },
      }
    );
  };

  const postCbtParCaptivePortal = (
    cbtParCaptivePortalConfig: CreateParCaptivePortalProfileWithFileObjToString
  ): Promise<AxiosResponse<ParseParCaptivePortal>> => {
    return axiosInstance.post<CreateParCaptivePortalProfileWithFileObjToString>(`${cbtApiUrl}api/par-captive-portals`, cbtParCaptivePortalConfig, {
      headers: {
        'content-type': 'multipart/form-data',
        Accept: '*/*',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const getCbtParScheduling = (): Promise<AxiosResponse<ParseParSchedulingProfile[]>> => {
    return axiosInstance.get<ParseParSchedulingProfile[]>(`${cbtApiUrl}api/par-scheduling-profiles`);
  };

  const deleteCbtParScheduling = (id: number): Promise<AxiosResponse<ParseParSchedulingProfile>> => {
    return axiosInstance.delete<ParseParSchedulingProfile>(`${cbtApiUrl}api/par-scheduling-profiles/${id}`);
  };

  const putCbtParScheduling = (
    id: number,
    cbtParSchedulingConfig: UpdateParSchedulingProfileDto
  ): Promise<AxiosResponse<ParseParSchedulingProfile>> => {
    return axiosInstance.put<ParseParSchedulingProfile>(`${cbtApiUrl}api/par-scheduling-profiles/${id}`, cbtParSchedulingConfig);
  };

  const postCbtParScheduling = (cbtParSchedulingConfig: CreateParSchedulingProfileDto): Promise<AxiosResponse<ParseParSchedulingProfile>> => {
    return axiosInstance.post<ParseParSchedulingProfile>(`${cbtApiUrl}api/par-scheduling-profiles`, cbtParSchedulingConfig);
  };

  const getCbtParRadios = (): Promise<AxiosResponse<ParseParRadio[]>> => {
    return axiosInstance.get<ParseParRadio[]>(`${cbtApiUrl}api/par-radios`);
  };

  const putCbtParRadios = (cbtParRadioConfig: UpdateRadioProfileDto): Promise<AxiosResponse<ParseParRadio[]>> => {
    return axiosInstance.put<ParseParRadio[]>(`${cbtApiUrl}api/par-radios/profile`, cbtParRadioConfig);
  };

  const postCbtParRadios = (cbtParRadioConfig: CreateRadioProfileDto): Promise<AxiosResponse<ParseParRadio[]>> => {
    return axiosInstance.post<ParseParRadio[]>(`${cbtApiUrl}api/par-radios/profile`, cbtParRadioConfig);
  };

  const deleteCbtParRadios = (cbtParRadioConfig: DeleteRadioProfileDto): Promise<AxiosResponse<ParseParRadio[]>> => {
    return axiosInstance.delete<ParseParRadio[]>(`${cbtApiUrl}api/par-radios/profile`, { data: cbtParRadioConfig });
  };

  const getCbtFirmwareBySerialNumber = (serialNumber: string): Promise<AxiosResponse> => {
    return axiosInstance.get(`${cbtApiUrl}api/owgw/firmware/${serialNumber}`);
  };

  const postCbtFirmwareBySerialNumber = (serialNumber: string): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/owgw/firmware/${serialNumber}`, {});
  };

  const getCheckNewestFirmwareBySerialNumber = (serialNumber: string): Promise<AxiosResponse> => {
    return axiosInstance.get(`${cbtApiUrl}api/firmware/${serialNumber}/checkNewest`);
  };

  const postUpgradeFirmwareBySerialNumber = (serialNumber: string, fwInfo: FirmwareResponse): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/firmware/${serialNumber}/upgrade`, fwInfo);
  };

  const postLedsBySerialNumber = (serialNumber: string): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/owgw/device/${serialNumber}/leds`, {
      serialNumber,
      when: 0,
      duration: 5,
      pattern: 'on',
    });
  };

  const postOnBoardBySerialNumber = (serialNumber: string): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/owgw/onBoard/${serialNumber}`, {});
  };

  const postRebootBySerialNumber = (serialNumber: string): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/owgw/device/${serialNumber}/reboot`, {
      serialNumber,
      when: 0,
    });
  };

  const postFactoryBySerialNumber = (serialNumber: string): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/owgw/device/${serialNumber}/factory`, {
      serialNumber,
      when: 0,
      keepRedirector: false,
    });
  };

  const getDashboard = (): Promise<AxiosResponse<Dashboard>> => {
    return axiosInstance.get<Dashboard>(`${cbtApiUrl}api/dashboard`);
  };

  const getDashboardServices = (): Promise<AxiosResponse<ControllerServiceDashboard>> => {
    return axiosInstance.get<ControllerServiceDashboard>(`${cbtApiUrl}api/dashboard/services`);
  };

  const getParInterfaces = (serialNumber: string): Promise<AxiosResponse<ParseParInterface[]>> => {
    return axiosInstance.get<ParseParInterface[]>(`${cbtApiUrl}api/par-interfaces/${serialNumber}/role/downstream`);
  };

  const postParInterfaces = (cbtNewParInterfaceConfig: CreateParInterfaceDto): Promise<AxiosResponse<ParseParInterface>> => {
    return axiosInstance.post<ParseParInterface>(`${cbtApiUrl}api/par-interfaces`, cbtNewParInterfaceConfig);
  };

  const postParInterfacesListBySerialNumber = (
    serialNumber: string,
    cbtNewParInterfaceArrayConfig: UpsertParInterfaceArrayDto
  ): Promise<AxiosResponse<ParseParInterface>> => {
    return axiosInstance.post<ParseParInterface>(
      `${cbtApiUrl}api/par-interfaces/networkList/applyChanges/${serialNumber}`,
      cbtNewParInterfaceArrayConfig
    );
  };

  const putParInterfaces = (id: number, cbtParInterfacesConfig: UpdateParInterfaceDto): Promise<AxiosResponse<ParseParInterface>> => {
    return axiosInstance.put<ParseParInterface>(`${cbtApiUrl}api/par-interfaces/${id}`, cbtParInterfacesConfig);
  };

  const deleteParInterfaces = (id: number): Promise<AxiosResponse<ParseParInterface>> => {
    return axiosInstance.delete<ParseParInterface>(`${cbtApiUrl}api/par-interfaces/${id}`);
  };

  const deleteParInterfacesListBySerialNumber = (serialNumber: string, idList: number[]): Promise<AxiosResponse<ParseParInterface>> => {
    return axiosInstance.delete<ParseParInterface>(`${cbtApiUrl}api/par-interfaces/networkList/${serialNumber}`, { data: idList });
  };

  const getParVlanProfiles = (): Promise<AxiosResponse<ParVlanProfile[]>> => {
    return axiosInstance.get<ParVlanProfile[]>(`${cbtApiUrl}api/par-vlan-profiles`);
  };

  const postParVlanProfile = (data: CreateParVlanProfileDto): Promise<AxiosResponse<ParVlanProfile>> => {
    return axiosInstance.post<ParVlanProfile>(`${cbtApiUrl}api/par-vlan-profiles`, data);
  };

  const updateParVlanProfile = (id: number, data: UpdateParVlanProfileDto): Promise<AxiosResponse<ParVlanProfile>> => {
    return axiosInstance.put<ParVlanProfile>(`${cbtApiUrl}api/par-vlan-profiles/${id}`, data);
  };

  const deleteParVlanProfileById = (id: number): Promise<AxiosResponse<ParVlanProfile>> => {
    return axiosInstance.delete<ParVlanProfile>(`${cbtApiUrl}api/par-vlan-profiles/${id}`);
  };

  const getBackupRestoreConfigurationByModelName = (): Promise<AxiosResponse> => {
    return axiosInstance.get(`${cbtApiUrl}api/systems/backup`, {
      headers: {
        'content-type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
        Accept: '*/*',
      },
      responseType: 'blob',
      timeout: 300 * 1000,
    });
  };

  const putBackupRestoreConfigurationByModelName = (file: FormData): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/systems/restore`, file, {
      headers: {
        'content-type': 'multipart/form-data',
        Accept: '*/*',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const putCbtUserUpdate = (cbtConfig: CbtUserUpdateDto): Promise<AxiosResponse> => {
    return axiosInstance.put(`${cbtApiUrl}api/users/cbtUserUpdate/${user.id}`, cbtConfig);
  };

  const putUserRole = (id: number, roleObject: { role: string }): Promise<AxiosResponse> => {
    return axiosInstance.put(`${cbtApiUrl}api/users/role/${id}`, roleObject);
  };

  const putCbtDeviceName = (serialNumber: string, cbtDeviceName: UpdateParDeviceNicknameDto): Promise<AxiosResponse> => {
    return axiosInstance.put(`${cbtApiUrl}api/par-devices/${serialNumber}/nickname`, cbtDeviceName);
  };

  const postEvent = (event: PostEventDto): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/owgw/event`, event);
  };

  const getEventList = (startDate: number, endDate: number): Promise<AxiosResponse<EventList>> => {
    return axiosInstance.get<EventList>(`${cbtApiUrl}api/owgw/eventList/startDate/${startDate}/endDate/${endDate}`);
  };

  const getQoeScoreTotalList = (startDate: number, endDate: number): Promise<AxiosResponse<QoeScoreTotalList>> => {
    return axiosInstance.get<QoeScoreTotalList>(`${cbtApiUrl}api/owgw/qoeScoreTotalList/startDate/${startDate}/endDate/${endDate}`);
  };

  const postUsersBindCloudAccout = (data: SetupCreateDto): Promise<AxiosResponse> => axiosInstance.post(`${cbtApiUrl}api/users/bind`, data);

  const postUsersUnBindCloudAccout = (data: SignInDto): Promise<AxiosResponse> => axiosInstance.post(`${cbtApiUrl}api/users/unbind`, data);

  const getDeviceMembers = (): Promise<AxiosResponse<User[]>> => axiosInstance.get(`${cbtApiUrl}api/users`);

  const postScheduleRebootBySerialNumber = (serialNumber: string, scheduleTime: number): Promise<AxiosResponse> =>
    axiosInstance.post(`${cbtApiUrl}api/owgw/device/${serialNumber}/reboot/${scheduleTime}`);

  const getParControllerPort = (controllerSerialNumber: string, portNumber: number): Promise<AxiosResponse<ParseParControllerPort>> => {
    return axiosInstance.get<ParseParControllerPort>(`${cbtApiUrl}api/par-controller-ports/${controllerSerialNumber}/port/${portNumber}`);
  };

  const putParControllerPort = (
    controllerSerialNumber: string,
    portNumber: number,
    cbtParControllerPortConfig: UpdateParControllerPortDto
  ): Promise<AxiosResponse<ParseParControllerPort>> => {
    return axiosInstance.put<ParseParControllerPort>(
      `${cbtApiUrl}api/par-controller-ports/${controllerSerialNumber}/port/${portNumber}`,
      cbtParControllerPortConfig
    );
  };

  const getParEapPort = (eapSerialNumber: string, portNumber: number): Promise<AxiosResponse<ParseParEapPort>> => {
    return axiosInstance.get<ParseParEapPort>(`${cbtApiUrl}api/par-eap-ports/${eapSerialNumber}/port/${portNumber}`);
  };

  const putParEapPort = (
    eapSerialNumber: string,
    portNumber: number,
    cbtParEapPortConfig: UpdateParEapPortDto
  ): Promise<AxiosResponse<ParseParEapPort>> => {
    return axiosInstance.put<ParseParEapPort>(`${cbtApiUrl}api/par-eap-ports/${eapSerialNumber}/port/${portNumber}`, cbtParEapPortConfig);
  };

  const getTssAggregateSumByTimestamp = (
    type: 'day' | 'week' | 'month',
    identified?: number
  ): Promise<AxiosResponse<TssAggregateTimestampTxRx[]>> => {
    let baseUrl = `${cbtApiUrl}api/tss-aggregate/${type}/sumByTimestamp`;

    if (identified !== undefined) {
      baseUrl += `?identified=${identified}`;
    }

    return axiosInstance.get(baseUrl);
  };

  const getTssAggregateSumByAppName = (type: 'day' | 'week' | 'month'): Promise<AxiosResponse<TssAggregateAppNameTxRx[]>> => {
    return axiosInstance.get(`${cbtApiUrl}api/tss-aggregate/${type}/sumByAppName`);
  };

  const postTssAggregateSumByAppNameQuery = (
    type: 'day' | 'week' | 'month',
    srcMacs: string[]
  ): Promise<AxiosResponse<TssAggregateAppNameTxRx[]>> => {
    return axiosInstance.post(`${cbtApiUrl}api/tss-aggregate/${type}/sumByAppName/query`, { srcMacs: srcMacs });
  };

  const getTssAggregateSumBySrcMac = (type: 'day' | 'week' | 'month'): Promise<AxiosResponse<TssAggregateSrcMacTxRx[]>> => {
    return axiosInstance.get(`${cbtApiUrl}api/tss-aggregate/${type}/sumBySrcMac`);
  };

  const getSystemStatisticDays = (serialNumber: string): Promise<AxiosResponse<SystemStatic[]>> => {
    return axiosInstance.get<SystemStatic[]>(`${cbtApiUrl}api/system-statistic-days/${serialNumber}`);
  };

  const getLatencyDays = (): Promise<AxiosResponse<LatencyDay[]>> => {
    return axiosInstance.get<LatencyDay[]>(`${cbtApiUrl}api/latency-days`);
  };

  const getClientQuality = (
    type: 'day' | 'week' | 'month',
    macAddr: string,
    connectType: number
  ): Promise<AxiosResponse<ClientQualityTimestampSnr[]>> => {
    return axiosInstance.get<ClientQualityTimestampSnr[]>(`${cbtApiUrl}api/client-quality/${type}?mac=${macAddr}&connectType=${connectType}`);
  };

  const getClientAllQuality = (type: 'day' | 'week' | 'month', macAddr: string): Promise<AxiosResponse<ClientQualityTimestampSnr[]>> => {
    return axiosInstance.get<ClientQualityTimestampSnr[]>(`${cbtApiUrl}api/client-quality/${type}?mac=${macAddr}`);
  };

  const getDeviceVerInfo = (): Promise<AxiosResponse<DeviceVerInfo>> => {
    return axiosInstance.get<DeviceVerInfo>(`${cbtApiUrl}api/deviceVerInfo`);
  };

  const getTaskCheckAndUpgrades = (): Promise<AxiosResponse<Task[]>> => {
    return axiosInstance.get<Task>(`${cbtApiUrl}api/task/checkAndUpgrades`);
  };

  const getSystems = (): Promise<AxiosResponse<ParseSystemWithoutParDeviceConfig>> => {
    return axiosInstance.get<ParseSystemWithoutParDeviceConfig>(`${cbtApiUrl}api/systems`);
  };

  const getSystemsParDeviceConfig = (configs: string[]): Promise<AxiosResponse<ParDeviceConfig>> => {
    let baseUrl = `${cbtApiUrl}api/systems/parDeviceConfig?`;

    if (configs.length > 0) {
      const configsStr = configs.map((param) => `c=${param}`).join('&');
      baseUrl += configsStr;
    }

    return axiosInstance.get<ParDeviceConfig>(baseUrl);
  };

  const putSystemsQuery = (systemsQuery: UpdateSystemDto): Promise<AxiosResponse<ParseSystemWithoutParDeviceConfig>> => {
    return axiosInstance.put<ParseSystemWithoutParDeviceConfig>(`${cbtApiUrl}api/systems/updateQuery`, systemsQuery);
  };

  const putSystemTemplateQuery = (systemsQuery: UpdateSystemTemplate): Promise<AxiosResponse<ParseSystemWithoutParDeviceConfig>> => {
    return axiosInstance.put<UpdateSystemTemplate>(`${cbtApiUrl}api/systems/template/updateLogo`, systemsQuery, {
      headers: {
        'content-type': 'multipart/form-data',
        Accept: '*/*',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const putSystemsParDeviceConfigQuery = (parDeviceConfigQuery: UpdateSystemParDeviceConfigDto): Promise<AxiosResponse<ParDeviceConfig>> => {
    return axiosInstance.put<ParDeviceConfig>(`${cbtApiUrl}api/systems/parDeviceConfig/updateQuery`, parDeviceConfigQuery);
  };

  const getSystemesSiteName = useCallback((): Promise<AxiosResponse<{ siteName: string }>> => {
    return axiosInstance.get<{ siteName: string }>(`${cbtApiUrl}api/systems/getSiteName`);
  }, [cbtApiUrl]);

  const getSysLogs = ({
    page,
    limit,
    sortBy,
    orderBy,
    searchColumn,
    searchContent,
  }: {
    page: number;
    limit: number;
    sortBy?: string;
    orderBy?: string;
    searchColumn?: string;
    searchContent?: string;
  }): Promise<AxiosResponse<SysLogsPagination>> => {
    let baseUrl = `${cbtApiUrl}api/sys-logs?page=${page}&limit=${limit}&`;

    if (sortBy !== undefined) {
      baseUrl += `sortBy=${sortBy}&`;
    }
    if (orderBy !== undefined) {
      baseUrl += `orderBy=${orderBy}&`;
    }
    if (searchColumn !== undefined) {
      baseUrl += `searchColumn=${searchColumn}&`;
    }
    if (searchContent !== undefined) {
      baseUrl += `searchContent=${searchContent}&`;
    }
    if (baseUrl.slice(-1) === '&') {
      baseUrl = baseUrl.slice(0, -1);
    }
    return axiosInstance.get<SysLogsPagination>(baseUrl);
  };

  const getSysLogDownload = (): Promise<AxiosResponse> => {
    return axiosInstance.get(`${cbtApiUrl}api/sys-logs/download`);
  };

  const getSysLogSendEmail = (): Promise<AxiosResponse> => {
    return axiosInstance.get(`${cbtApiUrl}api/sys-logs/sendEmail`);
  };

  const getSysLogsByCriteria = (criteriaDto: any) => {
    return axiosInstance.post<SysLogsPagination>(`${cbtApiUrl}api/sys-logs/search`, criteriaDto);
  };

  const getSysLogsRefresh = () => {
    return axiosInstance.get<SysLogsRefreshResp>(`${cbtApiUrl}api/sys-logs/refresh`);
  };

  const getCbtPoePowerCycle = (commandUuid: string): Promise<AxiosResponse<ParseCommandList>> => {
    return axiosInstance.get<ParseCommandList>(`${cbtApiUrl}api/owgw/poePowerCycle/${commandUuid}`);
  };

  const postCbtPoePowerCycle = (serialNumber: string, portNumber: number): Promise<AxiosResponse<CommandUuidResp>> => {
    return axiosInstance.post<CommandUuidResp>(`${cbtApiUrl}api/owgw/poePowerCycle/${serialNumber}/portNumber/${portNumber}`, {});
  };

  const getCbtCableDiagnostics = (commandUuid: string): Promise<AxiosResponse<ParseCommandList>> => {
    return axiosInstance.get<ParseCommandList>(`${cbtApiUrl}api/owgw/cableDiagnostics/${commandUuid}`);
  };

  const postCbtCableDiagnostics = (serialNumber: string, portNumber: number): Promise<AxiosResponse<CommandUuidResp>> => {
    return axiosInstance.post<CommandUuidResp>(`${cbtApiUrl}api/owgw/cableDiagnostics/${serialNumber}/portNumber/${portNumber}`, {});
  };

  const getCheckOwgwApiIsReady = (): Promise<AxiosResponse<boolean>> => {
    return axiosInstance.get<boolean>(`${cbtApiUrl}api/owgw/checkApiIsReady`);
  };

  const getControllerDhcpLeasesBySerialNumber = (serialNumber: string): Promise<AxiosResponse<DhcpLeaseInfo[]>> => {
    return axiosInstance.get<DhcpLeaseInfo[]>(`${cbtApiUrl}api/controllers/${serialNumber}/dhcpLeases`);
  };

  const getCheckApiIsReady = (): Promise<AxiosResponse<boolean>> => {
    return axiosInstance.get<boolean>(`${cbtApiUrl}api/checkApiIsReady`);
  };

  const getCbtControllerFwUpgradeProgress = async (serianNumber: string): Promise<ControllerFwUpgradeProgress> => {
    return fetch(`${cbtApiUrl}api/controllers/${serianNumber}/fwUpgradeProgress`, {
      headers: {
        Authorization: '',
        'Content-Type': 'application/json',
      },
    })
      .then(async (response) => {
        if (!response.ok && response.status === 400) {
          const errorData = await response.json();
          throw errorData;
        }
        return response.json();
      })
      .catch((error) => {
        throw error;
      });
  };

  const getCheckNewestBin = (): Promise<AxiosResponse> => {
    return axiosInstance.get(`${cbtApiUrl}api/systems/bin/checkNewest`);
  };

  const postUpgradeBin = (fwInfo: FirmwareResponse): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/systems/bin/upgrade`, fwInfo);
  };

  const postResetBin = (): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/systems/bin/reset`);
  };

  const postUploadBin = (file: FormData): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/systems/bin/upload`, file, {
      headers: {
        'content-type': 'multipart/form-data',
        Accept: '*/*',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const getCheckUpgradeBinIsCompleted = (): Promise<AxiosResponse<boolean>> => {
    return axiosInstance.get<boolean>(`${cbtApiUrl}api/systems/bin/upgrade/checkCompleted`);
  };

  const getCheckResetBinIsCompleted = (): Promise<AxiosResponse<boolean>> => {
    return axiosInstance.get<boolean>(`${cbtApiUrl}api/systems/bin/reset/checkCompleted`);
  };

  const getCheckUploadBinIsCompleted = (): Promise<AxiosResponse<boolean>> => {
    return axiosInstance.get<boolean>(`${cbtApiUrl}api/systems/bin/upload/checkCompleted`);
  };

  const getBinInfo = (): Promise<AxiosResponse<ResponseBinInfoDto>> => {
    return axiosInstance.get<boolean>(`${cbtApiUrl}api/systems/bin/info`);
  };

  const getOnboardedDevices = (): Promise<AxiosResponse<ComplexDevice[]>> => {
    return axiosInstance.get<ComplexDevice[]>(`${cbtApiUrl}api/par-devices/onboarded`);
  };

  const getCbtDiagnoseResult = async (diagnosticResultQuery: DiagnosticResultQuery): Promise<AxiosResponse<ResponseDiagnosticResult>> => {
    const { type, serialNumber = '', commandUuid = '' } = diagnosticResultQuery;
    if (type === DiagnosticType.SYSTEM) {
      return axiosInstance.get<ResponseDiagnosticResult>(`${cbtApiUrl}api/diagnostics/result?type=${type}`);
    }
    return axiosInstance.get<ResponseDiagnosticResult>(
      `${cbtApiUrl}api/diagnostics/result?type=${type}&serialNumber=${serialNumber}&commandUuid=${commandUuid}`
    );
  };

  const postPingDevice = async (serialNumber: string, isDiagnose: boolean = false): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}api/owgw/device/${serialNumber}/ping?isDiagnose=${isDiagnose}`);
  };

  const postCbtDiagnostic = async (diagnoseDto: DiagnoseDto): Promise<AxiosResponse<ResponsePostDiagnose>> => {
    return axiosInstance.post<ResponsePostDiagnose>(`${cbtApiUrl}api/diagnostics/diagnose`, diagnoseDto);
  };

  const postAbortCbtDiagnostic = async (abortDiagnosticDto: AbortDiagnoseDto): Promise<AxiosResponse<ResponseDiagnosticResult>> => {
    return axiosInstance.post<ResponseDiagnosticResult>(`${cbtApiUrl}api/diagnostics/abortDiagnose`, abortDiagnosticDto);
  };

  const putCbtDiagnosticTimeout = async (timeoutDto: UpdateDiagnoseTimeoutDto): Promise<AxiosResponse<ResponseDiagnosticResult>> => {
    return axiosInstance.put<ResponseDiagnosticResult>(`${cbtApiUrl}api/diagnostics/timeout`, timeoutDto);
  };

  const getAiFae = async () => {
    try {
      await axiosInstance.post(`${cbtApiUrl}api/aifae/connect`, {
        accessToken: getItem('access_token'),
        refreshToken: getItem('refresh_token'),
      });
      window.location.replace(aifaeDomain ? 'https://' + aifaeDomain : '');
    } catch (error) {
      throw error;
    }
  };

  useEffect(() => {
    if (token.length > 0 && cbtApiUrl && (buildVersion !== BuildVersionList.portal || (buildVersion === BuildVersionList.portal && siteId))) {
      getUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildVersion, token, cbtApiUrl, siteId]);

  useEffect(() => {
    if (buildVersion === BuildVersionList.portal && portalToken) {
      setToken(portalToken);
    }
  }, [buildVersion, portalToken]);

  useEffect(() => {
    if (cbtApiUrl && search.length > 0) {
      signinByCloud();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cbtApiUrl, search]);

  useEffect(() => {
    setItem('networkDrafts', JSON.stringify(networkDrafts));
  }, [networkDrafts]);

  useEffect(() => {
    setItem('controllerQueryConfigDataDrafts', JSON.stringify(controllerQueryConfigDataDrafts));
  }, [controllerQueryConfigDataDrafts]);

  useEffect(() => {
    // 為了刷新 countryCode, radioChannelWhiteList，供 RadioSetting.tsx 顯示
    if (buildVersion === BuildVersionList.portal && siteId && countryCode === '') {
      getDefaultConfig();
    }
  }, [buildVersion, siteId, countryCode, getDefaultConfig]);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        currentLng,
        setCurrentLng,
        networkDrafts,
        setNetworkDrafts,
        controllerQueryConfigDataDrafts,
        setControllerQueryConfigDataDrafts,
        // api part
        signUpByUser,
        signUpByOldUser,
        signInByUser,
        logout,
        updateTokenAfterLogin,
        getUser,
        getWanInfo,
        getControllersConfigurationQueryBySerialNumber,
        putControllersConfigurationQueryBySerialNumber,
        getEapsConfigurationQueryBySerialNumber,
        putEapsConfigurationQueryBySerialNumber,
        getSwitchsConfigurationQueryBySerialNumber,
        putSwitchsConfigurationQueryBySerialNumber,
        deleteDeviceBySerialNumber,
        getRttyBySerialNumber,
        getRttyAuthorized,
        getRttyFontSize,
        getEaps,
        getEapProfiles,
        putEapProfileMapIds,
        getEapBySerialNumber,
        postEapConfigurationCopy,
        postCbtPing,
        postCbtTraceroute,
        postCbtNslookup,
        getCbtWifiEnv,
        getCbtWifiScan,
        postCbtWifiScan,
        getTopologyTreeNode,
        getControllerInfo,
        getClients,
        getClientByMacAddr,
        getCaptiveClients,
        getCaptiveClientsStatus,
        postCaptiveClients,
        getSwitches,
        getSwitchBySerialNumber,
        postSwitchConfigurationCopy,
        getParSwitchPort,
        putParSwitchPort,
        putParSwitchPorts,
        getParSwitchPortLldpStatus,
        getParSwitchBindingTable,
        getCbtParSsids,
        putCbtParSsids,
        postCbtParSsids,
        deleteCbtParSsids,
        getCbtParRadius,
        putCbtParRadius,
        postCbtParRadius,
        deleteCbtParRadius,
        getCbtParCaptivePortal,
        deleteCbtParCaptivePortal,
        putCbtParCaptivePortal,
        postCbtParCaptivePortal,
        getCbtParScheduling,
        deleteCbtParScheduling,
        putCbtParScheduling,
        postCbtParScheduling,
        getCbtParRadios,
        putCbtParRadios,
        postCbtParRadios,
        deleteCbtParRadios,
        getCbtFirmwareBySerialNumber,
        postCbtFirmwareBySerialNumber,
        getCheckNewestFirmwareBySerialNumber,
        postUpgradeFirmwareBySerialNumber,
        postLedsBySerialNumber,
        postOnBoardBySerialNumber,
        postRebootBySerialNumber,
        postScheduleRebootBySerialNumber,
        postFactoryBySerialNumber,
        getDashboard,
        getDashboardServices,
        getParInterfaces,
        postParInterfaces,
        postParInterfacesListBySerialNumber,
        putParInterfaces,
        deleteParInterfaces,
        deleteParInterfacesListBySerialNumber,
        getParVlanProfiles,
        postParVlanProfile,
        updateParVlanProfile,
        deleteParVlanProfileById,
        getBackupRestoreConfigurationByModelName,
        putBackupRestoreConfigurationByModelName,
        putCbtUserUpdate,
        putUserRole,
        putCbtDeviceName,
        getEventList,
        postEvent,
        getQoeScoreTotalList,
        postUsersBindCloudAccout,
        postUsersUnBindCloudAccout,
        getDeviceMembers,
        getParControllerPort,
        putParControllerPort,
        getParEapPort,
        putParEapPort,
        getTssAggregateSumByTimestamp,
        getTssAggregateSumByAppName,
        postTssAggregateSumByAppNameQuery,
        getTssAggregateSumBySrcMac,
        getSystemStatisticDays,
        getLatencyDays,
        getClientQuality,
        getClientAllQuality,
        getDeviceVerInfo,
        getTaskCheckAndUpgrades,
        getSystems,
        getSystemsParDeviceConfig,
        putSystemsQuery,
        putSystemTemplateQuery,
        putSystemsParDeviceConfigQuery,
        getSystemesSiteName,
        getSysLogs,
        getSysLogDownload,
        getSysLogSendEmail,
        getSysLogsByCriteria,
        getSysLogsRefresh,
        getCbtPoePowerCycle,
        postCbtPoePowerCycle,
        getCbtCableDiagnostics,
        postCbtCableDiagnostics,
        getCheckOwgwApiIsReady,
        getControllerDhcpLeasesBySerialNumber,
        getCheckApiIsReady,
        getCbtControllerFwUpgradeProgress,
        getCheckNewestBin,
        postUpgradeBin,
        postResetBin,
        postUploadBin,
        getCheckUpgradeBinIsCompleted,
        getCheckResetBinIsCompleted,
        getCheckUploadBinIsCompleted,
        getBinInfo,
        getOnboardedDevices,
        getCbtDiagnoseResult,
        postPingDevice,
        postCbtDiagnostic,
        postAbortCbtDiagnostic,
        putCbtDiagnosticTimeout,
        getAiFae,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => React.useContext(AuthContext);
